import { Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { FileServerService } from '@app/core/services/file-server.service';
import type { FileConversionStatus } from '@app/core/services/file-server.types';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { DocArcGet, DocArcService, DocumentCategoryItem, GetDocArcParams } from '@app/services/doc-arc.service';

@Component({
    selector: 'tt-doc-arc',
    templateUrl: './doc-arc.component.html',
    styleUrls: ['./doc-arc.component.css'],
    encapsulation: ViewEncapsulation.None,

    standalone: false,
})
export class DocArcComponent implements OnInit, OnChanges {
    @Input()
    public params?: DocArcParams;

    public page?: DocArcPage;

    public mobileViewerPage?: DocArcPage;

    public documentCategories: DocumentCategoryItem[] = [];

    public readonly bottomSheetMinHeight = '10rem';

    public readonly informationSkeletonRows = Array.from({ length: 8 });

    public loading = false;

    public fileError = '';

    constructor(
        private docarc: DocArcService,
        private fileServer: FileServerService,
        private appSettings: AppSettingsService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    private async init(): Promise<void> {
        if (!this.params) return;

        this.loading = true;
        this.fileError = '';
        this.page = undefined;
        this.mobileViewerPage = undefined;
        this.documentCategories = [];

        try {
            await this.appSettings.ensureIsReady();

            const getParams: GetDocArcParams = { docarc_keyno: this.params.docarc_keyno };
            const [docarc, documentCategories] = await Promise.all([this.docarc.getDocArc(getParams), this.docarc.getDocumentCategories()]);
            this.documentCategories = documentCategories;
            this.navigator?.setHeading?.(docarc.doctitle || docarc.docfilename);

            try {
                const file = await this.fileServer.getFileDetails({ fileName: docarc.docfilename, maxWidth: 1200, maxHeight: 600 });
                if (!file) throw Error('File details were not returned');

                this.page = { ...file, ...docarc };
                this.mobileViewerPage = this.getMobileViewerPage(this.page);
            } catch (error) {
                this.page = { ...docarc };
                this.mobileViewerPage = this.page;
                this.fileError = error instanceof Error ? error.message : String(error);
            }
        } finally {
            this.loading = false;
        }
    }

    private getMobileViewerPage(page: DocArcPage): DocArcPage {
        if (!page.imageLink || !page.docfilename.toLowerCase().endsWith('.pdf')) return page;

        const [url, fragment = ''] = page.imageLink.split('#', 2);
        const parameters = new URLSearchParams(fragment);
        parameters.set('view', 'FitH');

        return { ...page, imageLink: `${url}#${parameters.toString()}` };
    }

    ngOnInit(): void {
        if (this.navigator) {
            this.params = this.navigator.params as unknown as DocArcParams;
            void this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']?.currentValue) void this.init();
    }

    public async onOfficeConversionCompleted(result: FileConversionStatus): Promise<void> {
        if (!this.page) return;

        await this.docarc.updateDocArcFileName({ docarcKeyno: this.page.docarc_keyno, docFileName: result.fileName });
    }
}

export interface DocArcParams {
    argtype?: string;
    argvalue?: string;
    docarc_keyno: number | `${number}`;
}

export interface DocArcPage extends DocArcGet {
    fileName?: string | null;
    fileItemId?: string | null;
    imageLink?: string | null;
    contentType?: string | null;
    extension?: string | null;
    localFileName?: string | null;
    thumbnailLink?: string | null;
    wopiIsConverted?: boolean | null;
}
